<template>
  <div
    class="w-full h-full flex flex-col relative animate-fade-in"
    :class="theme.text"
  >
    <div class="shrink-0 h-12 flex items-center justify-between px-4 z-10">
      <button @click="$emit('back')">
        <icon-base
          name="ChevronLeft"
          class="w-6 h-6 opacity-60 hover:opacity-100"
        ></icon-base>
      </button>
      <span class="font-bold">{{ t.stats_title || "统计" }}</span>
      <button :title="t.share || '分享'" @click="$refs.share.open()">
        <icon-base
          name="Share"
          class="w-5 h-5 opacity-60 hover:opacity-100"
        ></icon-base>
      </button>
    </div>
    <div class="flex-1 overflow-y-auto px-4 pb-4">
      <div
        class="p-4 rounded-2xl mb-6"
        :class="theme.isDark ? 'bg-white/5' : 'bg-black/5'"
      >
        <div class="flex justify-between items-start mb-4">
          <div class="text-xs opacity-50 uppercase tracking-wider font-bold">
            {{ t.week_trend }}
          </div>
          <div class="text-right">
            <div class="text-xs font-bold opacity-70">
              {{
                selectedDate.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })
              }}
            </div>
            <div
              class="text-[11px] font-bold mt-0.5"
              :class="theme.isDark ? 'text-amber-400' : 'text-amber-600'"
            >
              {{ t.flow_time }} {{ fmtMins(selectedDayMinutes) }}
            </div>
          </div>
        </div>
        <div class="grid grid-cols-7 gap-1.5">
          <button
            v-for="(day, i) in heatmapData"
            :key="i"
            @click="selectedDate = day.date"
            class="aspect-square rounded-md flex items-center justify-center text-[10px] font-medium transition-all relative"
            :class="[
              day.isSelected
                ? 'ring-2 ring-offset-1 ring-amber-500 scale-110 z-10'
                : '',
              day.isToday ? 'border border-amber-500/50' : '',
            ]"
            :style="{ backgroundColor: day.bgColor, color: day.color }"
          >
            {{ day.dayNum }}
          </button>
        </div>
        <div class="text-[10px] opacity-40 mt-3 text-center">
          {{ t.flow_heatmap_hint }}
        </div>
      </div>
      <div class="space-y-3">
        <div
          class="text-xs opacity-50 uppercase tracking-wider font-bold flex justify-between"
        >
          <span>{{
            isSameDay(selectedDate, new Date()) ? t.today_record : t.day_record
          }}</span
          ><span
            >{{ t.total_count }} {{ selectedDayRecords.length }}
            {{ t.count_unit }}</span
          >
        </div>
        <div
          v-if="selectedDayRecords.length === 0"
          class="text-center py-12 opacity-40 text-xs flex flex-col items-center"
        >
          {{ t.no_record }}
        </div>
        <div v-else>
          <div
            v-for="item in selectedDayRecords"
            :key="item.id"
            class="flex items-center justify-between p-3 rounded-xl"
            :class="theme.isDark ? 'bg-white/5' : 'bg-black/5'"
          >
            <div class="flex items-center gap-3">
              <div
                class="w-10 h-10 rounded-full flex items-center justify-center"
                :class="theme.isDark ? 'bg-white/10' : 'bg-white'"
              >
                <icon-base name="Brain" class="w-5 h-5 opacity-70"></icon-base>
              </div>
              <div>
                <div
                  class="text-sm font-bold"
                  :class="theme.isDark ? 'text-white' : 'text-slate-800'"
                >
                  {{ item.tag }}
                </div>
                <div class="text-[10px] opacity-50">
                  {{
                    new Date(item.date).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  }}
                </div>
              </div>
            </div>
            <div class="text-right">
              <div
                class="font-bold"
                :class="theme.isDark ? 'text-amber-400' : 'text-amber-600'"
              >
                +{{ item.duration }}m
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 分享卡片弹层（抽成独立组件，与计时完成页共用） -->
    <share-card
      ref="share"
      :t="t"
      :history="history"
      :summary="summary"
      :month-heatmap="monthHeatmap"
      :is-same-day="isSameDay"
    ></share-card>
  </div>
</template>

<script>
import ShareCard from "./ShareCard.vue";

export default {
  components: { ShareCard },
  props: ["theme", "t", "history", "summary", "monthHeatmap", "isSameDay"],
  data() {
    return {
      selectedDate: new Date(),
    };
  },
  computed: {
    selectedDayRecords() {
      return this.history.filter((h) =>
        this.isSameDay(new Date(h.date), this.selectedDate)
      );
    },
    // 选中当日的心流（专注）总时长（分钟）
    selectedDayMinutes() {
      return this.selectedDayRecords.reduce((acc, h) => acc + h.duration, 0);
    },
    heatmapData() {
      return this.monthHeatmap.map((day) => {
        // 按当日心流时长着色：120 分钟（2h）为最深，越久越深
        let opacity = 0.1;
        if (day.minutes > 0)
          opacity = Math.min(0.2 + (day.minutes / 120) * 0.8, 1);
        const isSelected = this.isSameDay(day.date, this.selectedDate);
        const bgColor =
          day.minutes > 0
            ? this.theme.isDark
              ? `rgba(245, 158, 11, ${opacity})`
              : `rgba(217, 119, 6, ${opacity})`
            : this.theme.isDark
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.05)";
        const color =
          day.minutes > 0
            ? "#fff"
            : this.theme.isDark
            ? "rgba(255,255,255,0.3)"
            : "rgba(0,0,0,0.3)";
        return { ...day, isSelected, bgColor, color };
      });
    },
  },
  methods: {
    // 分钟数格式化为「Xh Ym / Xh / Ym」
    fmtMins(m) {
      if (!m) return "0m";
      const h = Math.floor(m / 60);
      const mm = m % 60;
      if (h > 0) return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
      return `${mm}m`;
    },
  },
};
</script>
